import {
  CABINET_INTERIOR_SECTION_TYPE as SECTION_TYPE
} from "../constants/cabinetInteriorConstants";

export const CABINET_TOE_KICK_HEIGHT = 4.5;
export const CABINET_TOE_KICK_DEPTH = 3;
export const TALL_CABINET_TOE_KICK_HEIGHT = CABINET_TOE_KICK_HEIGHT;
export const TALL_CABINET_DOORS_PER_SECTION = 2;

const roundHeight = (height) => Math.round(height * 100) / 100;

const getPositiveQuantity = (quantity) => {
  const parsedQuantity = parseInt(quantity, 10);
  return Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 0;
};

const isDoorSection = (section) => {
  if (!section) {
    return false;
  }

  return section.type === SECTION_TYPE.box
    ? section.hasDoor !== false
    : section.type === SECTION_TYPE.shelf && section.hasDoor === true;
};

const getTallCabinetInteriorLayout = (cabinetShape) => {
  const shapeCabinet = cabinetShape && cabinetShape.shapeCabinet;

  if (!shapeCabinet) {
    return null;
  }

  if (typeof shapeCabinet.getCabinetInteriorLayout === "function") {
    return shapeCabinet.getCabinetInteriorLayout();
  }

  return null;
};

export const getTallCabinetDoorSectionCount = (sections = []) => {
  const countedDoorGroups = new Set();

  return sections.reduce((doorSectionCount, section) => {
    if (!isDoorSection(section)) {
      return doorSectionCount;
    }

    const doorGroupId = section.doorExtensionGroupId;
    if (doorGroupId && countedDoorGroups.has(doorGroupId)) {
      return doorSectionCount;
    }
    if (doorGroupId) {
      countedDoorGroups.add(doorGroupId);
    }

    return doorSectionCount + 1;
  }, 0);
};

// Door-extension sections share one continuous door, so their heights must be
// combined before hardware quantities are calculated.
export const getTallCabinetDoorHeightsFromSections = (sections = []) => {
  const doorHeights = [];
  const extendedDoorIndexes = new Map();

  sections.forEach((section) => {
    if (!isDoorSection(section)) {
      return;
    }

    const sectionHeight = Number(section.height);
    if (!Number.isFinite(sectionHeight) || sectionHeight <= 0) {
      return;
    }

    const doorGroupId = section.doorExtensionGroupId;
    if (!doorGroupId) {
      doorHeights.push(sectionHeight);
      return;
    }

    if (!extendedDoorIndexes.has(doorGroupId)) {
      extendedDoorIndexes.set(doorGroupId, doorHeights.length);
      doorHeights.push(0);
    }

    const doorIndex = extendedDoorIndexes.get(doorGroupId);
    doorHeights[doorIndex] += sectionHeight;
  });

  return doorHeights.map(roundHeight);
};

export const getTallCabinetDoorHeights = (cabinetShape) => {
  const layout = getTallCabinetInteriorLayout(cabinetShape);

  return layout && Array.isArray(layout.sections)
    ? getTallCabinetDoorHeightsFromSections(layout.sections)
    : [];
};

export const getTallCabinetDoorHeight = (cabinetShape) => {
  const layout = getTallCabinetInteriorLayout(cabinetShape);
  if (!layout || !Array.isArray(layout.sections)) {
    return 0;
  }

  return layout.sections.reduce((totalHeight, section) => {
    if (!isDoorSection(section)) {
      return totalHeight;
    }

    const sectionHeight = Number(section.height);
    return Number.isFinite(sectionHeight) && sectionHeight > 0
      ? totalHeight + sectionHeight
      : totalHeight;
  }, 0);
};

export const getCabinetInteriorHeight = (cabinetHeight) => {
  const normalizedHeight = Number(cabinetHeight);
  if (!normalizedHeight || normalizedHeight <= TALL_CABINET_TOE_KICK_HEIGHT) {
    return 1;
  }

  return roundHeight(normalizedHeight - TALL_CABINET_TOE_KICK_HEIGHT);
};

export const hasEditableCabinetInteriorLayout = (layout) => {
  return Boolean(
    layout
    && layout.isEditableInterior === true
    && Array.isArray(layout.sections)
    && layout.sections.length > 0
  );
};

export const hasTallCabinetInteriorLayout = (cabinetShape) => {
  return hasEditableCabinetInteriorLayout(getTallCabinetInteriorLayout(cabinetShape));
};

/**
 * Normalize a tall-cabinet layout into quantities shared by pricing and UI.
 * Linked box sections form one extended door section.
 */
export const getTallCabinetInteriorQuantitiesFromLayout = (layout) => {
  if (!layout || !Array.isArray(layout.sections) || layout.sections.length === 0) {
    return {shelves: 0, drawers: 0, doorSections: 0, sourceDoorSections: 0};
  }

  const sections = layout.sections;

  const quantities = sections.reduce((sectionQuantities, section) => {
    const quantity = getPositiveQuantity(section && section.quantity);

    if (section && section.type === SECTION_TYPE.shelf) {
      sectionQuantities.shelves += quantity;
    } else if (section && section.type === SECTION_TYPE.drawer) {
      sectionQuantities.drawers += quantity;
    }

    return sectionQuantities;
  }, {shelves: 0, drawers: 0});

  const doorSections = getTallCabinetDoorSectionCount(sections);
  const parsedSourceDoorSections = getPositiveQuantity(layout.sourceDoorSectionCount);

  return {
    ...quantities,
    doorSections,
    sourceDoorSections: parsedSourceDoorSections || doorSections,
  };
};

export const getTallCabinetInteriorQuantities = (cabinetShape) => {
  return getTallCabinetInteriorQuantitiesFromLayout(
    getTallCabinetInteriorLayout(cabinetShape)
  );
};

const getHandleQuantitiesFromInterior = (interior) => {
  const doors = interior.doorSections * TALL_CABINET_DOORS_PER_SECTION;
  const drawers = interior.drawers;

  return {
    doors,
    drawers,
    total: doors + drawers,
  };
};

// Editable tall cabinets define their current fronts in the interior layout,
// so handle resources must not depend on legacy product filters.
export const getTallCabinetHandleQuantities = (cabinetShape) => {
  return getHandleQuantitiesFromInterior(getTallCabinetInteriorQuantities(cabinetShape));
};

// Popup pricing works with serialized SQFT snapshots, not live ShapeCabinet instances.
export const getExportedTallCabinetInteriorQuantities = (exportedShape) => {
  const layout = exportedShape
    && exportedShape.shapeCabinet
    && exportedShape.shapeCabinet.cabinetInteriorLayout;

  return getTallCabinetInteriorQuantitiesFromLayout(layout);
};

export const hasExportedTallCabinetInteriorLayout = (exportedShape) => {
  const layout = exportedShape
    && exportedShape.shapeCabinet
    && exportedShape.shapeCabinet.cabinetInteriorLayout;

  return hasEditableCabinetInteriorLayout(layout);
};

export const getExportedTallCabinetDoorHeights = (exportedShape) => {
  const layout = exportedShape
    && exportedShape.shapeCabinet
    && exportedShape.shapeCabinet.cabinetInteriorLayout;

  return layout && Array.isArray(layout.sections)
    ? getTallCabinetDoorHeightsFromSections(layout.sections)
    : [];
};

export const calculateTallCabinetDoors = (interior, filterQuantities) => {
  if (interior.sourceDoorSections <= 0) {
    return 0;
  }

  if (filterQuantities.doors <= 0) {
    return interior.doorSections * TALL_CABINET_DOORS_PER_SECTION;
  }

  return Math.round(
    filterQuantities.doors * interior.doorSections / interior.sourceDoorSections
  );
};

export const calculateTallCabinetDrawers = (interior) => interior.drawers;

export const calculateTallCabinetShelves = (interior) => interior.shelves;

// Drawer slides are bundled one-for-one with the derived drawer feature.
export const calculateTallCabinetSlideSets = (interior) => interior.drawers;

export const calculateTallCabinetKnobs = (interior) => {
  return getHandleQuantitiesFromInterior(interior).total;
};

/**
 * Door height up to 36″: 2 hinges
   Over 36″ and up to 60″: 3 hinges
   Over 60″: 4 hinges
 * @param doorHeight
 * @return {number|number}
 */
export const getHingesPerDoor = (doorHeight) => {
  if (doorHeight <= 36) {
    return 2;
  }

  return doorHeight <= 60 ? 3 : 4;
};

export const calculateTallCabinetHinges = (interior, filterQuantities, doorHeights = null) => {
  if (Array.isArray(doorHeights)) {
    if (doorHeights.length === 0) {
      return 0;
    }

    const doorsPerSection = filterQuantities.doors > 0 && interior.sourceDoorSections > 0
      ? filterQuantities.doors / interior.sourceDoorSections
      : TALL_CABINET_DOORS_PER_SECTION;

    const hingesPerSection = doorHeights.reduce((totalHinges, doorHeight) => {
      return totalHinges + getHingesPerDoor(doorHeight);
    }, 0);

    return Math.round(hingesPerSection * doorsPerSection);
  }

  if (filterQuantities.doors <= 0) {
    return 0;
  }

  const currentDoors = calculateTallCabinetDoors(interior, filterQuantities);
  return Math.round(filterQuantities.hinges / filterQuantities.doors * currentDoors);
};

export const calculateTallCabinetHingesForShape = (cabinetShape, filterQuantities) => {
  if (hasTallCabinetInteriorLayout(cabinetShape)) {
    return calculateTallCabinetHinges(
      getTallCabinetInteriorQuantities(cabinetShape),
      filterQuantities,
      getTallCabinetDoorHeights(cabinetShape)
    );
  }

  const cabinetHeight = typeof cabinetShape.getHeight === "function"
    ? cabinetShape.getHeight()
    : cabinetShape.shapeParams && cabinetShape.shapeParams.height;

  return filterQuantities.doors * getHingesPerDoor(getCabinetInteriorHeight(cabinetHeight));
};
